/**
 * Application shell and composition root.
 *
 * {@link AppShell} chooses how the app authenticates: with Microsoft Entra
 * configured it stands up a real sign-in and talks to the platform API over
 * HTTP; with no configuration it falls back to a configuration-required
 * notice. The decision is made once, here, so the rest of the tree never
 * branches on it.
 *
 * @module components/AppShell
 */

import { type EntraAppClient } from "@/auth/entra-auth-context";
import { createEntraApp, type EntraBrowserConfig } from "@/auth/entra-browser";
import { selectPlatformRuntime } from "@/platform";
import type { createAppRouter } from "@/router/routes";

import {
  ConfigurationRequiredScreen,
  type DevelopmentAuthUi,
  EntraModeShell,
  resolveConfiguredEntra,
} from "./EntraShell";

export interface AppShellProps {
  /** The data router mounted once authenticated. Built once, up front, in
   * `index.tsx` — see `router/routes.tsx`. */
  router: ReturnType<typeof createAppRouter>;
  /**
   * Builds a router at another basename, for when the tenant a path names is
   * refused. Absent, that refusal stays on screen.
   */
  createRouter?: (basename: string) => ReturnType<typeof createAppRouter>;
  /**
   * Entra configuration override. `undefined` resolves it from the build-time
   * environment; `null` forces the unconfigured state; a config forces Entra
   * mode. The override lets the composition root be tested without a live tenant.
   */
  entraConfig?: EntraBrowserConfig | null;
  /** MSAL app factory. Injectable so the sign-in lifecycle can be faked in tests. */
  createApp?: (config: EntraBrowserConfig) => EntraAppClient;
  /** Runtime selector. Injectable so the authenticated path can avoid real HTTP in tests. */
  selectRuntime?: typeof selectPlatformRuntime;
  /** Local-only account chooser supplied by the explicit development identity mode. */
  developmentAuth?: DevelopmentAuthUi;
}

export function AppShell({
  router,
  createRouter = undefined,
  entraConfig = undefined,
  createApp = createEntraApp,
  selectRuntime = selectPlatformRuntime,
  developmentAuth = undefined,
}: AppShellProps) {
  const config = entraConfig === undefined ? resolveConfiguredEntra() : (entraConfig ?? undefined);

  if (!config) {
    return <ConfigurationRequiredScreen />;
  }

  return (
    <EntraModeShell
      config={config}
      createApp={createApp}
      selectRuntime={selectRuntime}
      developmentAuth={developmentAuth}
      router={router}
      createRouter={createRouter}
    />
  );
}
